// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN GROUPS — corps de la modale utilisateur (rendu pur)
// Extrait de page-admin-groups.ts (découpe god-function `showUserModal` 2026-07) :
// construction PURE du corps HTML de la modale (cases équipes/groupes, chips
// managers, formulaire). Aucun état de closure — toutes les entrées (immuables
// au moment du rendu) sont passées via `opts`. Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t, userLabel.

import type { UserEntityFull, UserGroup } from './page-admin-groups.js';

export interface UserModalBodyOpts {
  user: UserEntityFull | null;
  isNew: boolean;
  isTeamChecked: boolean;
  isActiveChecked: boolean;
  teams: ReadonlyArray<UserEntityFull>;
  currentTeamIds: number[];
  managerCandidates: ReadonlyArray<UserEntityFull>;
  currentManagerIds: number[];
  availableGroups: UserGroup[];
  currentGroupIds: Set<number>;
  canEditGroups: boolean;
  currentDeparture: string;
  currentObjectRights: string;
}

/** Corps HTML complet de la modale utilisateur (création / édition). */
export function buildUserModalBody(opts: UserModalBodyOpts): string {
  const {
    user,
    isNew,
    isTeamChecked,
    isActiveChecked,
    teams,
    currentTeamIds,
    managerCandidates,
    currentManagerIds,
    availableGroups,
    currentGroupIds,
    canEditGroups,
    currentDeparture,
    currentObjectRights,
  } = opts;

  const teamCheckboxes =
    teams
      .map(
        (tm) => `
    <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;font-weight:400">
      <input type="checkbox" name="um-team-ids" value="${tm.id}" ${currentTeamIds.includes(tm.id) ? 'checked' : ''}>
      ${esc(userLabel(tm, false))}
    </label>`,
      )
      .join('') ||
    `<span style="color:var(--text3);font-size:12px">${esc(t('user_modal.no_teams'))}</span>`;

  // Chips initiales des managers — mêmes classes/structure que raci-picker
  // pour que showRaciPicker(...) puisse lire/réécrire dans `chips-managers`.
  const managerChipsHtml =
    currentManagerIds.length > 0
      ? currentManagerIds
          .map((id) => managerCandidates.find((u) => u.id === id))
          .filter((u): u is UserEntityFull => Boolean(u))
          .map(
            (u) =>
              `<span class="raci-chip" data-uid="${u.id}" data-field="managers" style="display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);font-size:11px;padding:2px 7px;border-radius:10px;margin:2px;cursor:default">${esc(userLabel(u, true))}<button data-remove-raci="${u.id}" data-field="managers" style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button></span>`,
          )
          .join('')
      : `<span class="raci-empty" style="color:var(--text3);font-size:11px">${esc(t('actionModal.raci.empty'))}</span>`;

  const groupCheckboxes = availableGroups.length
    ? availableGroups
        .map((g) => {
          const adminBadge = g.is_admin_group
            ? ` <span style="background:#fef3c7;color:#92400e;font-size:9px;padding:1px 5px;border-radius:8px">${esc(t('user_modal.group.admin_badge'))}</span>`
            : '';
          const disabled = canEditGroups ? '' : 'disabled';
          return `<label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:${canEditGroups ? 'pointer' : 'default'};font-weight:400;font-size:13px">
          <input type="checkbox" name="um-group-ids" value="${g.id}" ${currentGroupIds.has(g.id) ? 'checked' : ''} ${disabled}>
          <span>${esc(g.name)}${adminBadge}</span>
        </label>`;
        })
        .join('')
    : `<span style="color:var(--text3);font-size:12px">${esc(t('user_modal.no_groups'))}</span>`;

  return `
    <div class="form-row">
      <div class="form-group" style="flex:2"><label>${esc(t('user_modal.field.last_name'))}</label>
        <input type="text" id="um-last-name" value="${esc(user ? user.last_name || '' : '')}" placeholder="${esc(t('user_modal.field.last_name.placeholder'))}"></div>
      <div class="form-group" style="flex:1"><label>${esc(t('user_modal.field.trigram'))} <span style="color:var(--text3);font-weight:400">${esc(t('user_modal.field.trigram.unique'))}</span></label>
        <input type="text" id="um-trigram" value="${esc(user ? user.trigram || '' : '')}" placeholder="${esc(t('user_modal.field.trigram.placeholder'))}" maxlength="5" style="text-transform:uppercase"></div>
    </div>
    <div class="form-group" id="um-first-name-group" style="margin-top:8px${isTeamChecked ? ';display:none' : ''}">
      <label>${esc(t('user_modal.field.first_name'))}</label>
      <input type="text" id="um-first-name" value="${esc(user ? user.first_name || '' : '')}" placeholder="${esc(t('user_modal.field.first_name.placeholder'))}">
    </div>
    <div class="form-row" style="margin-top:10px;align-items:flex-start;gap:16px">
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer">
          <input type="checkbox" id="um-is-team" ${isTeamChecked ? 'checked' : ''}>
          ${esc(t('user_modal.field.is_team'))}
        </label>
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer">
          <input type="checkbox" id="um-is-external" ${user && user.is_external ? 'checked' : ''}>
          ${esc(t('user_modal.field.is_external'))}
        </label>
      </div>
      <div class="form-group" id="um-active-group"${isTeamChecked ? ' style="display:none"' : ''}>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer">
          <input type="checkbox" id="um-is-active" ${isActiveChecked ? 'checked' : ''}>
          ${esc(t('user_modal.field.is_active'))}
        </label>
      </div>
    </div>
    <div class="form-group" id="um-team-group" style="margin-top:8px${isTeamChecked ? ';display:none' : ''}">
      <label>${esc(t('user_modal.field.teams'))}</label>
      <div style="padding:8px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg3)">
        ${teamCheckboxes}
      </div>
    </div>
    <div class="form-group" id="um-managers-group" style="margin-top:8px${isTeamChecked ? '' : ';display:none'}">
      <label>${esc(t('user_modal.field.managers'))}</label>
      <div class="raci-block" id="raci-managers" style="border:1px solid var(--border2);border-radius:var(--radius);padding:6px 8px;margin-top:4px;display:flex;align-items:flex-start;gap:6px">
        <div class="raci-chips" id="chips-managers" style="flex:1;min-height:22px;display:flex;flex-wrap:wrap;gap:2px">
          ${managerChipsHtml}
        </div>
        <button type="button" class="raci-add-btn btn btn-secondary btn-icon" id="um-managers-add" style="flex-shrink:0;font-size:14px;line-height:1;padding:3px 7px;align-self:flex-start" title="${esc(t('actionModal.raci.btnAddTitle'))}">+</button>
      </div>
      <div style="font-size:11px;color:var(--text3);margin-top:4px">${esc(t('user_modal.field.managers.help'))}</div>
    </div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('user_modal.field.email'))}</label>
      <input type="email" id="um-email" value="${esc(user ? user.email || '' : '')}" placeholder="${esc(t('user_modal.field.email.placeholder'))}"></div>
    <div class="form-row" style="margin-top:8px">
      <div class="form-group" style="flex:1"><label>${esc(t('user_modal.field.role'))}</label>
        <select id="um-role">
          <option value="member" ${!user || user.role === 'member' ? 'selected' : ''}>${esc(t('user_modal.role.member'))}</option>
          <option value="lead"   ${user && user.role === 'lead' ? 'selected' : ''}>${esc(t('user_modal.role.lead'))}</option>
          <option value="admin"  ${user && user.role === 'admin' ? 'selected' : ''}>${esc(t('user_modal.role.admin'))}</option>
        </select></div>
      <div class="form-group" style="flex:1${isTeamChecked ? ';display:none' : ''}" id="um-departure-group">
        <label>${esc(t('user_modal.field.departure'))} <span style="color:var(--text3);font-weight:400">${esc(t('user_modal.field.departure.note'))}</span></label>
        <input type="date" id="um-departure-date" value="${esc(currentDeparture || '')}">
      </div>
    </div>
    <div class="form-group" id="um-object-rights-group" style="margin-top:8px${isTeamChecked ? ';display:none' : ''}">
      <label>${esc(t('user_modal.field.object_rights'))}</label>
      <select id="um-object-rights">
        <option value="DC" ${currentObjectRights === 'DC' ? 'selected' : ''}>${esc(t('user_modal.object_rights.DC'))}</option>
        <option value="DM" ${currentObjectRights === 'DM' ? 'selected' : ''}>${esc(t('user_modal.object_rights.DM'))}</option>
        <option value="DD" ${currentObjectRights === 'DD' ? 'selected' : ''}>${esc(t('user_modal.object_rights.DD'))}</option>
        <option value="DS" ${currentObjectRights === 'DS' ? 'selected' : ''}>${esc(t('user_modal.object_rights.DS'))}</option>
      </select>
      <div style="font-size:11px;color:var(--text3);margin-top:4px">${esc(t('user_modal.field.object_rights.help'))}</div>
    </div>
    <div class="form-group" id="um-groups-group" style="margin-top:8px${isTeamChecked ? ';display:none' : ''}">
      <label>${esc(t('user_modal.field.groups'))} ${canEditGroups ? '' : `<span style="color:var(--text3);font-weight:400;font-size:11px">${esc(t('user_modal.field.groups.readonly'))}</span>`}</label>
      <div style="max-height:160px;overflow-y:auto;padding:8px 12px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg3)">
        ${groupCheckboxes}
      </div>
    </div>
    ${
      !isNew && !isTeamChecked
        ? `
    <div class="form-group" id="um-invite-group" style="margin-top:12px;padding-top:10px;border-top:1px dashed var(--border)">
      <label style="display:flex;align-items:center;justify-content:space-between;gap:8px">
        <span>${esc(t('user_modal.invite.label'))}</span>
        <span style="display:flex;gap:6px">
          <button type="button" class="btn btn-secondary" id="um-invite-btn" style="font-size:11px;padding:4px 10px">${esc(t('user_modal.invite.btn.generate'))}</button>
          <button type="button" class="btn btn-secondary" id="um-invite-send-btn" style="font-size:11px;padding:4px 10px">${esc(t('user_modal.invite.btn.send'))}</button>
        </span>
      </label>
      <div id="um-invite-result" style="margin-top:8px;display:none">
        <div style="display:flex;gap:6px;align-items:center">
          <input type="text" id="um-invite-url" readonly style="flex:1;font-size:11px;padding:6px;font-family:monospace">
          <button type="button" class="btn btn-secondary" id="um-invite-copy" style="font-size:11px;padding:4px 10px">${esc(t('user_modal.invite.btn.copy'))}</button>
        </div>
        <div style="font-size:11px;color:var(--text3);margin-top:4px">
          ${esc(t('user_modal.invite.help'))}
        </div>
      </div>
    </div>`
        : ''
    }`;
}
